/** Persisted voice output; default off. Legacy `app_voice_enabled` is removed on first read. */
export const VOICE_OUTPUT_PREF_KEY = 'aotf_voice_output';
/** Persisted chat microphone (speech-to-text); default off. */
export const VOICE_INPUT_PREF_KEY = 'aotf_voice_input';

const LEGACY_VOICE_KEY = 'app_voice_enabled';
/** Once set, we stop forcing the product default; bump value to reset all clients to off one more time. */
const VOICE_POLICY_KEY = 'aotf_voice_policy';
const VOICE_POLICY_OFF_BY_DEFAULT = 'off-by-default-v1';
const MIC_POLICY_KEY = 'aotf_mic_policy';
const MIC_POLICY_OFF_BY_DEFAULT = 'off-by-default-v1';

let _voicePrefMigrated = false;

function migrateVoiceOutputLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    try {
      localStorage.removeItem(LEGACY_VOICE_KEY);
    } catch {
      /* ignore */
    }
    let raw = localStorage.getItem(VOICE_OUTPUT_PREF_KEY);
    if (raw !== '0' && raw !== '1') {
      localStorage.setItem(VOICE_OUTPUT_PREF_KEY, '0');
      raw = '0';
    }
    // One-time: older builds defaulted voice on or wrote non-binary values; align with default off.
    if (localStorage.getItem(VOICE_POLICY_KEY) !== VOICE_POLICY_OFF_BY_DEFAULT) {
      localStorage.setItem(VOICE_OUTPUT_PREF_KEY, '0');
      localStorage.setItem(VOICE_POLICY_KEY, VOICE_POLICY_OFF_BY_DEFAULT);
    }
  } catch {
    /* ignore */
  }
}

function migrateMicInputLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(VOICE_INPUT_PREF_KEY);
    if (raw !== '0' && raw !== '1') {
      localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
    }
    if (localStorage.getItem(MIC_POLICY_KEY) !== MIC_POLICY_OFF_BY_DEFAULT) {
      localStorage.setItem(VOICE_INPUT_PREF_KEY, '0');
      localStorage.setItem(MIC_POLICY_KEY, MIC_POLICY_OFF_BY_DEFAULT);
    }
  } catch {
    /* ignore */
  }
}

function readBinaryPref(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw !== '0' && raw !== '1') {
      localStorage.setItem(key, '0');
      return false;
    }
    return raw === '1';
  } catch {
    return false;
  }
}

/** Voice is on only when explicitly set to `'1'` (avoids accidental on from `'true'`, junk, or legacy keys). */
export function readVoiceOutputPreference() {
  if (typeof window === 'undefined') return false;
  if (!_voicePrefMigrated) {
    try {
      migrateVoiceOutputLocalStorage();
      migrateMicInputLocalStorage();
    } finally {
      _voicePrefMigrated = true;
    }
  }
  return readBinaryPref(VOICE_OUTPUT_PREF_KEY);
}

/** Microphone / speech-to-text in chat; on only when explicitly set to `'1'`. */
export function readVoiceInputPreference() {
  if (typeof window === 'undefined') return false;
  if (!_voicePrefMigrated) {
    try {
      migrateVoiceOutputLocalStorage();
      migrateMicInputLocalStorage();
    } finally {
      _voicePrefMigrated = true;
    }
  }
  return readBinaryPref(VOICE_INPUT_PREF_KEY);
}

/** Saved mic + voice output toggles (both default off until explicitly enabled). */
export function readSavedAudioPreferences() {
  return {
    voiceOutputEnabled: readVoiceOutputPreference(),
    micInputEnabled: readVoiceInputPreference(),
  };
}

/** Run voice + mic default-off migrations once at app boot. */
export function migrateAudioPreferencesLocalStorage() {
  if (typeof window === 'undefined') return;
  if (!_voicePrefMigrated) {
    try {
      migrateVoiceOutputLocalStorage();
      migrateMicInputLocalStorage();
    } finally {
      _voicePrefMigrated = true;
    }
  }
}
